import { useMutation, useQueryClient } from "@tanstack/react-query";
import { RsvpsPersonalScreenProps } from "navigation/types";
import {
  acceptRsvpOfferRequest,
  cancelRsvpRequest,
  createRsvpRequest,
  RsvpStatusDetails,
  rsvpEventsQueryKey,
  rsvpStatusQueryKey,
  updateRsvpQuantityRequest,
  useFetchRsvpEventsQuery,
  useFetchRsvpStatusQuery,
} from "queries/rsvp.queries";
import React, { useState } from "react";
import { ActivityIndicator, Text, View } from "react-native";
import toast from "utils/toast";
import RsvpCancelModal from "./components/RsvpCancelModal";
import RsvpClosedState from "./components/RsvpClosedState";
import RsvpEligibleView from "./components/RsvpEligibleView";
import RsvpEventHeader from "./components/RsvpEventHeader";
import RsvpEventImage from "./components/RsvpEventImage";
import RsvpEventPicker from "./components/RsvpEventPicker";
import RsvpMessage from "./components/RsvpMessage";
import RsvpScaffold from "./components/RsvpScaffold";
import RsvpStatusCard from "./components/RsvpStatusCard";
import { getRsvpErrorCode } from "./rsvpErrorCode";
import { useRsvpColors } from "./theme";

const Loading = () => {
  const colors = useRsvpColors();
  return (
    <RsvpScaffold>
      <View style={{ alignItems: "center" }}>
        <ActivityIndicator size="large" color={colors.accent} />
      </View>
    </RsvpScaffold>
  );
};

const RsvpsPersonal = (props: RsvpsPersonalScreenProps) => {
  const {
    navigation,
    route: {
      params: { eventSlug, playerId, eventId: preSelectedEventId },
    },
  } = props;

  const colors = useRsvpColors();
  const queryClient = useQueryClient();
  const {
    data: status,
    isLoading,
    isError,
    error,
    refetch,
  } = useFetchRsvpStatusQuery(eventSlug, playerId);

  // Events are needed for the ELIGIBLE picker and to resolve the reserved
  // event's date/venue for the reservation states (the status payload omits
  // those). Not needed for NOT_ELIGIBLE/EXPIRED/FULL. Passing playerId narrows
  // the list to events this player is eligible for (plus their reserved event,
  // if any).
  const needsEvents =
    !!status &&
    status.status !== "NOT_ELIGIBLE" &&
    status.status !== "EXPIRED" &&
    status.status !== "FULL";
  const { data: events, isLoading: isLoadingEvents } = useFetchRsvpEventsQuery(
    eventSlug,
    needsEvents,
    playerId,
  );

  const { mutateAsync: createRsvp, isLoading: isConfirming } =
    useMutation(createRsvpRequest);
  const { mutateAsync: cancelRsvp, isLoading: isCancelling } =
    useMutation(cancelRsvpRequest);
  const { mutateAsync: updateQuantity, isLoading: isUpdatingQuantity } =
    useMutation(updateRsvpQuantityRequest);
  const { mutateAsync: acceptOffer, isLoading: isAccepting } = useMutation(
    acceptRsvpOfferRequest,
  );

  const [cancelModalVisible, setCancelModalVisible] = useState(false);

  const setStatus = (details: RsvpStatusDetails) => {
    queryClient.setQueryData(rsvpStatusQueryKey(eventSlug, playerId), details);
  };

  if (isLoading) {
    return <Loading />;
  }

  if (isError || !status) {
    const code = getRsvpErrorCode(error);
    if (code === "ACCOUNT_MEMBER_NOT_FOUND") {
      return (
        <RsvpMessage
          icon="user-x"
          tone="warning"
          title="We couldn't find that Player ID."
          body="Double-check your Player ID and try again."
          actionText="Re-enter Player ID"
          onAction={() => navigation.navigate("RsvpsGeneral", { eventSlug })}
        />
      );
    }
    if (code === "EVENT_NOT_FOUND") {
      return (
        <RsvpMessage
          icon="alert-circle"
          tone="danger"
          title="We couldn't find this event."
          body="Please check your link and try again."
        />
      );
    }
    return (
      <RsvpMessage
        icon="wifi-off"
        tone="warning"
        title="Something went wrong."
        body="Please try again."
        actionText="Try Again"
        onAction={() => refetch()}
      />
    );
  }

  const handleConfirm = async (
    eventId: number,
    quantity: number,
    policyAccepted: boolean,
  ) => {
    try {
      const updated = await createRsvp({
        eventSlug,
        playerId,
        eventId,
        quantity,
        policyAccepted,
      });
      setStatus(updated);
    } catch (err) {
      const code = getRsvpErrorCode(err);
      if (code === "RSVP_NOT_ELIGIBLE") {
        setStatus({ ...status, status: "NOT_ELIGIBLE" });
        return;
      }
      if (code === "RSVP_EXPIRED") {
        setStatus({ ...status, status: "EXPIRED" });
        return;
      }
      if (code === "RSVP_CAPACITY_REACHED") {
        // The event filled up (no waitlist) since the page loaded. Other
        // same-named events may still be open, so re-resolve the status and
        // the per-event full flags rather than assuming the whole slug is full.
        toast.error("This event is full and no longer taking RSVPs.");
        queryClient.invalidateQueries(rsvpEventsQueryKey(eventSlug, playerId));
        refetch();
        return;
      }
      toast.error("Something went wrong. Please try again.");
    }
  };

  const handleUpdateQuantity = async (quantity: number) => {
    if (status.bookingRequestId == null) return;
    try {
      const updated = await updateQuantity({
        eventSlug,
        playerId,
        bookingRequestId: status.bookingRequestId,
        quantity,
      });
      setStatus(updated);
      toast.success("Your RSVP has been updated.");
    } catch (err) {
      const code = getRsvpErrorCode(err);
      if (
        code === "BOOKING_REQUEST_NOT_PENDING" ||
        code === "RSVP_EXPIRED" ||
        code === "RSVP_CANCELLATION_CUTOFF_PASSED"
      ) {
        toast.error("This RSVP can no longer be modified.");
        refetch();
        return;
      }
      if (code === "RSVP_QUANTITY_EXCEEDS_MAX") {
        toast.error("That number of seats exceeds the maximum allowed.");
        refetch();
        return;
      }
      if (code === "RSVP_CAPACITY_REACHED") {
        toast.error("Not enough spots remain to add that many seats.");
        refetch();
        return;
      }
      toast.error("Something went wrong. Please try again.");
    }
  };

  const handleCancel = async () => {
    if (status.bookingRequestId == null) return;
    setCancelModalVisible(false);
    try {
      const updated = await cancelRsvp({
        eventSlug,
        playerId,
        bookingRequestId: status.bookingRequestId,
      });
      setStatus(updated);
    } catch (err) {
      const code = getRsvpErrorCode(err);
      if (
        code === "BOOKING_REQUEST_NOT_PENDING" ||
        code === "RSVP_CANCELLATION_CUTOFF_PASSED"
      ) {
        toast.error("This RSVP can no longer be cancelled.");
        refetch();
        return;
      }
      toast.error("Something went wrong. Please try again.");
    }
  };

  const handleAcceptOffer = async () => {
    if (status.bookingRequestId == null) return;
    try {
      const updated = await acceptOffer({
        eventSlug,
        playerId,
        bookingRequestId: status.bookingRequestId,
      });
      setStatus(updated);
      toast.success("You're in! Your spot has been confirmed.");
    } catch (err) {
      const code = getRsvpErrorCode(err);
      if (code === "RSVP_OFFER_EXPIRED") {
        toast.error(
          "This offer has expired. You've been returned to the waitlist.",
        );
        refetch();
        return;
      }
      if (code === "BOOKING_REQUEST_NOT_PENDING") {
        toast.error("This offer is no longer open.");
        refetch();
        return;
      }
      toast.error("Something went wrong. Please try again.");
    }
  };

  const hasReservation =
    status.status === "CONFIRMED" ||
    status.status === "WAITLISTED" ||
    status.status === "OFFERED" ||
    status.status === "PENDING";

  const renderBody = () => {
    // Precedence: an existing reservation always shows status, never the form.
    if (hasReservation) {
      const reservedEvent = events?.find((e) => e.eventId === status.eventId);
      const multiEvent = (events?.length ?? 0) > 1;

      // Multiple same-named events: show the full list with the reserved one
      // marked, and offer only cancel (a player may hold one RSVP per slug).
      if (multiEvent && events) {
        return (
          <View>
            {/* Same-named events: show the shared name once; each tile in the
                list below carries only its date */}
            <RsvpEventHeader
              event={reservedEvent ?? events[0]}
              showDate={false}
            />
            <RsvpEventPicker
              events={events}
              modelValue={null}
              reservedEventId={status.eventId}
              reservedLabel={
                status.status === "WAITLISTED"
                  ? "Waitlist"
                  : status.status === "OFFERED"
                    ? "Spot Offered"
                    : "Your RSVP"
              }
              readOnly
              label="Events"
            />
            <RsvpStatusCard
              details={status}
              maxQuantity={reservedEvent?.maxQuantity ?? null}
              playerId={playerId}
              showDetails={false}
              onCancel={() => setCancelModalVisible(true)}
              isCancelling={isCancelling}
              onUpdateQuantity={handleUpdateQuantity}
              isUpdatingQuantity={isUpdatingQuantity}
              onAcceptOffer={handleAcceptOffer}
              isAccepting={isAccepting}
            />
          </View>
        );
      }

      return (
        <RsvpStatusCard
          details={status}
          event={reservedEvent}
          maxQuantity={reservedEvent?.maxQuantity ?? null}
          playerId={playerId}
          onCancel={() => setCancelModalVisible(true)}
          isCancelling={isCancelling}
          onUpdateQuantity={handleUpdateQuantity}
          isUpdatingQuantity={isUpdatingQuantity}
          onAcceptOffer={handleAcceptOffer}
          isAccepting={isAccepting}
        />
      );
    }

    if (status.status === "EXPIRED") {
      return (
        <RsvpClosedState
          icon="clock"
          title="RSVPs have closed for this event."
          body="The RSVP window has passed. If you have questions, contact the event organizer."
        />
      );
    }

    if (status.status === "FULL") {
      return (
        <RsvpClosedState
          icon="ban"
          title="This event is full."
          body="RSVP capacity has been reached and this event is not taking a waitlist. If you have questions, contact the event organizer."
        />
      );
    }

    if (status.status === "NOT_ELIGIBLE") {
      return (
        <RsvpClosedState
          icon="ban"
          title="You're not eligible for this event."
          body="If you think this is a mistake, contact the event organizer."
        />
      );
    }

    // ELIGIBLE
    if (isLoadingEvents || !events) {
      return (
        <View style={{ alignItems: "center" }}>
          <ActivityIndicator size="large" color={colors.accent} />
        </View>
      );
    }
    if (events.length === 0) {
      return (
        <RsvpMessage
          icon="alert-circle"
          tone="danger"
          title="We couldn't find this event."
          body="Please check your link and try again."
        />
      );
    }
    return (
      <RsvpEligibleView
        events={events}
        preSelectedEventId={preSelectedEventId}
        disclaimer={status.disclaimer}
        cancellationPolicyTitle={status.cancellationPolicyTitle}
        cancellationPolicyBody={status.cancellationPolicyBody}
        onConfirm={handleConfirm}
        isConfirming={isConfirming}
      />
    );
  };

  // Prefer the reserved event's image; otherwise the first slug event that has one
  const eventImageUrl =
    events?.find((e) => e.eventId === status.eventId)?.eventImageUrl ??
    events?.find((e) => e.eventImageUrl)?.eventImageUrl;

  return (
    <RsvpScaffold>
      <RsvpEventImage url={eventImageUrl} />
      {renderBody()}
      <RsvpCancelModal
        visible={cancelModalVisible}
        onKeep={() => setCancelModalVisible(false)}
        onConfirm={handleCancel}
        loading={isCancelling}
        mode={status.status === "OFFERED" ? "decline" : "cancel"}
      />
    </RsvpScaffold>
  );
};

export default RsvpsPersonal;
